import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import Button from "../../../../../components/base/button/button";
import {Paper, SvgIcon, ToggleButton, ToggleButtonGroup} from "@mui/material";
import SearchInput from "../../../../../components/base/input/search_input";
import Utils, {formatMoney} from "../../../../../../core/services/utils/utils";
import {Add, Edit, EventAvailableRounded, RuleRounded} from "@mui/icons-material";
import UpsertPriceMatrixDialog, {PriceMatrixSources, PriceMatrixTypes} from "../../../../../components/dialogs/upsert-price-matrix";
import Api from "../../../../../../core/services/api/api";
import {PriceMatricesDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import {ReactComponent as RemoveIcon} from "../../../../../../assets/images/remove.svg";
import IconButton from "../../../../../components/base/button/icon_button";
import PartnerDataGrid from "../../../../../containers/data-grid";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useCachedPriceLevelsDictionaryController from "../../../../../hooks/use-price-level-dictionary";
import ProductInformationPopover from "../../../../../components/app-specific/draggable-popover/product-information-popover";
import {useAppDispatch, useAppSelector} from "../../../../../redux";
import {useParams} from "react-router-dom";
import SelectExportPurposeDialog, {selectExportPurposes} from "../../../../../components/dialogs/select-export-purpose";
import DateUtils from "../../../../../../core/services/date-utils";
import UploadBizImportFileDialog from "../../../../../components/dialogs/upload-bizimport-file";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ToastService from "../../../../../../core/services/toast";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import UIUtils from "../../../../../utils";
import useAccessLevels from "../../../../../hooks/use-access-levels";

const initialSelectExportPurposeDialogState = {open: false, close: () => void 0, onSelect: () => void 0}
const initialSelectUploadBizImportFileDialogState = {open: false, close: () => void 0, onSelect: () => void 0, title: null, description: null, parse: () => void 0, validate: () => void 0}
const initialUpsertDialogState = {open: false, close: () => void 0, onSave: () => void 0, matrix: null}

/**
 * When false, BizImport re-import export is allowed without `sales.exportPriceMatrices`.
 * Record-keeping CSV export always requires `sales.exportPriceMatrices`.
 */
const requireExportPriceMatricesForReimport = false;

const PriceMatricesListView = () => {
    const params = useParams();
    const [loading, setLoading] = useState(true);
    const [matrices, setMatrices] = useState([]);
    const [keywords, setKeywords] = useState('');
    const [removingIds, setRemovingIds] = useState([]);
    const [selectExportPurposeDialogState, setSelectExportPurposeDialogState] = useState(initialSelectExportPurposeDialogState)
    const [uploadBizImportFileDialogState, setUploadBizImportFileDialogState] = useState(initialSelectUploadBizImportFileDialogState)
    const [upsertDialogState, setUpsertDialogState] = useState(initialUpsertDialogState);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();
    const operatorAccessLevels = useAccessLevels();
    const canManagePriceMatrices = operatorAccessLevels.packages.sales.managePriceMatrices;
    const canExportPriceMatrices = operatorAccessLevels.packages.sales.exportPriceMatrices;
    const canExportRecordKeeping = canExportPriceMatrices;
    const canExportReimport = canExportPriceMatrices || !requireExportPriceMatricesForReimport;
    const canShowMatrixExport = canExportRecordKeeping || canExportReimport;

    const availableExportPurposes = useMemo(() => [
        ...(canExportRecordKeeping ? [selectExportPurposes.recordKeeping] : []),
        ...(canExportReimport ? [selectExportPurposes.reImporting] : []),
    ], [canExportRecordKeeping, canExportReimport]);

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('price-matrices-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const {priceLevelDict} = useCachedPriceLevelsDictionaryController({loadDict: true});

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        if (!!params.customerId) {
            if (!defaultCustomer?.code)
                return;
            configureNotebookTags((tags) => tags.customerPriceMatrixList(defaultCustomer?.code));
            configureBehavioralTool((tags) => tags.customerPriceMatrixList(defaultCustomer?.code));
        } else {
            configureNotebookTags((tags) => tags.priceMatrixList);
            configureBehavioralTool((tags) => tags.priceMatrixList);
        }
    }, [params.customerId, defaultCustomer, configureNotebookTags, configureBehavioralTool]);

    const singularTitle = useMemo(() => {
        if (params.customerId) {
            return 'Price Matrix';
        }
        return "Tier Pricing"
    }, [params.customerId])

    const multipleTitle = useMemo(() => {
        if (params.customerId) {
            return 'Price Matrices';
        }
        return "Tier Pricing"
    }, [params.customerId])

    const dataGridSaveConfig = useMemo(() => {
        if (params.customerId) {
            return ({
                name: "CustomerPriceMatricesListViewDataGrid",
                version: canManagePriceMatrices ? 1 : 2,
            });
        }
        return ({
            name: "GeneralPriceMatricesListViewDataGrid",
            version: canManagePriceMatrices ? 1 : 2,
        });
    }, [params.customerId, canManagePriceMatrices])

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(canManagePriceMatrices ? [
                {
                    name: PriceMatricesDataGridCellKeys.remove,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.number,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    reorderable: false,
                    pinned: true,
                    width: 0,
                },
                {
                    name: PriceMatricesDataGridCellKeys.edit,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.number,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    reorderable: false,
                    pinned: true,
                    width: 0,
                },
            ] : []),
            {
                title: "Type",
                name: PriceMatricesDataGridCellKeys.type,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Target",
                name: PriceMatricesDataGridCellKeys.target,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Change",
                name: PriceMatricesDataGridCellKeys.change,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Based on",
                name: PriceMatricesDataGridCellKeys.basedOn,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Start Date",
                name: PriceMatricesDataGridCellKeys.startDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                sortable: false,
            },
            {
                title: "End Date",
                name: PriceMatricesDataGridCellKeys.endDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                sortable: false,
            },
        ], [canManagePriceMatrices])

    const [dataGridColumns, , dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const [currentPage, setCurrentPage] = useState(DefaultPaginationInfo.currentPage);
    const [pageSize, setPageSize] = useState(dataGridPageSize ?? DefaultPaginationInfo.pageSize);
    const [viewMode, toggleViewMode] = useState('all');

    const transformedMatrices = useMemo(() => {
        return matrices.map(matrix => {
            const type = matrix.part !== null
                ? PriceMatrixTypes.part
                : matrix.partCategory !== null
                    ? PriceMatrixTypes.category
                    : matrix.priceLevel !== null
                        ? PriceMatrixTypes.priceLevel
                        : PriceMatrixTypes.general;
            let target;
            switch (type.id) {
                case PriceMatrixTypes.part.id:
                    target = matrix.part?.partNo;
                    break;
                case PriceMatrixTypes.category.id:
                    target = matrix.partCategory?.title;
                    break;
                case PriceMatrixTypes.priceLevel.id:
                    target = priceLevelDict?.find(p => p.priceLevelId === matrix.priceLevel?.id)?.title ?? matrix.priceLevel?.title;
                    break;
                case PriceMatrixTypes.general.id:
                default:
                    target = PriceMatrixTypes.general.title;
                    break;
            }
            let change = matrix.isPercentage ? `${matrix.value}%` : formatMoney(matrix.value);
            if (matrix.isFixedValue) {
                change = change.concat(' (Fixed)');
            }
            const basedOn = matrix.isBasedOnCost ? PriceMatrixSources.cost.title : PriceMatrixSources.finalPrice.title;
            return {change, basedOn, type, target, matrix};
        });
    }, [matrices, priceLevelDict])

    const [filteredMatrices, activeMatricesLength, allMatricesLength] = useMemo(() => {
        let result = transformedMatrices;
        let activeMatricesLength = 0;
        let allMatricesLength = result.length;
        if (viewMode === 'active') {
            result = result.filter(({matrix}) => (
                (!matrix.endDate || DateUtils.serverToLocal(matrix.endDate).isSameOrAfter(DateUtils.localNow(), 'day')) &&
                (!matrix.startDate || DateUtils.serverToLocal(matrix.startDate).isSameOrBefore(DateUtils.localNow(), 'day'))
            ));
            activeMatricesLength = result.length;
        } else {
            activeMatricesLength = result.filter(({matrix}) => (
                (!matrix.endDate || DateUtils.serverToLocal(matrix.endDate).isSameOrAfter(DateUtils.localNow(), 'day')) &&
                (!matrix.startDate || DateUtils.serverToLocal(matrix.startDate).isSameOrBefore(DateUtils.localNow(), 'day'))
            )).length;
        }
        if (!!keywords?.trim()?.length) {
            const lowerKeywords = keywords?.trim()?.toLowerCase() ?? '';
            result = result.filter(({type, change, basedOn, target, matrix}) => !!(
                change?.toLowerCase()?.includes(lowerKeywords)
                || basedOn?.toLowerCase()?.includes(lowerKeywords)
                || target?.toString()?.toLowerCase()?.includes(lowerKeywords)
                || type?.title?.toLowerCase()?.includes(lowerKeywords)
                || target?.toString()?.toLowerCase()?.includes(lowerKeywords)
                || (matrix.startDate ? DateUtils.serverToLocal(matrix.startDate).format(DateUtils.dateTimeFormat).toLowerCase().includes(lowerKeywords) : false)
                || (matrix.endDate ? DateUtils.serverToLocal(matrix.endDate).format(DateUtils.dateTimeFormat).toLowerCase().includes(lowerKeywords) : false)
            ))
        }
        return [result, activeMatricesLength, allMatricesLength];
    }, [keywords, transformedMatrices, viewMode])

    const visibleMatrices = useMemo(() => {
        if (!transformedMatrices?.length) {
            return [];
        }
        const start = Math.min(Math.max((currentPage - 1) * pageSize, 0), transformedMatrices.length);
        const end = Math.min((currentPage) * pageSize, transformedMatrices.length);
        return filteredMatrices.slice(start, end)
    }, [transformedMatrices.length, currentPage, pageSize, filteredMatrices])

    const paginationInfo = useMemo(() => ({
        ...DefaultPaginationInfo,
        pageSize: pageSize,
        currentPage: Math.min(currentPage, Math.ceil(filteredMatrices.length / pageSize) || 1),
        length: filteredMatrices.length,
    }), [pageSize, currentPage, filteredMatrices.length]);

    /**
     * With every change in keywords, resets the current page to 1.
     */
    useEffect(() => {
        setCurrentPage(1);
    }, [keywords]);

    /**
     * Fetches the list of price matrices from the server.
     *
     * * if customerId exists, only fetches the price matrices of the respective customer.
     */
    const getPriceMatrices = useCallback(async ({customer}) => {
        setLoading(true);
        let response;
        if (customer) {
            response = await Api.getCustomerPriceMatrices(params.customerId);
        } else {
            response = await Api.getSystemPriceMatrices();
        }
        if (!isMounted())
            return;
        const data = response?.data ?? [];
        setMatrices(data);
        setCurrentPage(1);
        setLoading(false);
    }, [params.customerId, isMounted])

    /**
     * Fetches the list of price matrices of a customer if the customerId exists.
     */
    useEffect(() => {
        if (params.customerId) {
            void getPriceMatrices({customer: true})
        }
    }, [params.customerId, getPriceMatrices]);

    /**
     * Fetches the list of price matrices if customerId does not exist.
     */
    useEffect(() => {
        if (!params.customerId) {
            void getPriceMatrices({});
        }
    }, [getPriceMatrices, params.customerId]);

    /**
     * Shows the modal for editing the specified price matrix and sets the selected price matrix to the selected one.
     * @param matrix {any}
     */
    const onEditPriceMatrix = useCallback((matrix) => {
        if (!operatorAccessLevels.packages.sales.managePriceMatrices) {
            return;
        }
        setUpsertDialogState({
            open: true,
            matrix: matrix,
            close: () => setUpsertDialogState(initialUpsertDialogState),
            onSave: (newMatrix) => setMatrices(prevState => prevState.map(e => e.id === matrix.id ? newMatrix : e)),
        })
    }, [operatorAccessLevels.packages.sales.managePriceMatrices])

    /**
     * Shows the modal for adding a new price matrix and sets the selected price matrix to null.
     */
    const onAddMatrix = useCallback(() => {
        if (!operatorAccessLevels.packages.sales.managePriceMatrices) {
            return;
        }
        setUpsertDialogState({
            open: true,
            matrix: undefined,
            close: () => setUpsertDialogState(initialUpsertDialogState),
            onSave: (matrices) => setMatrices(prevState => [...matrices, ...prevState]),
        })
    }, [operatorAccessLevels.packages.sales.managePriceMatrices])

    /**
     * Calls the api for removing a specific price matrix and if the result is successful, removes that price matrix
     * from the list.
     * @param {number} matrixId
     */
    const removePriceMatrix = useCallback(async (matrixId) => {
        if (!operatorAccessLevels.packages.sales.managePriceMatrices) {
            return;
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `the selected ${singularTitle}`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                setRemovingIds(prevState => [...prevState, matrixId]);
                const response = await Api.removePriceMatrix(matrixId);
                if (!isMounted())
                    return;
                if (response?.resultFlag) {
                    setMatrices(prevState => prevState.filter(e => e.id !== matrixId));
                }
                setRemovingIds(prevState => prevState.filter(e => e !== matrixId));
                return response?.resultFlag ?? false
            }
        }));
    }, [dispatch, isMounted, operatorAccessLevels.packages.sales.managePriceMatrices, singularTitle])

    /**
     * Opens the export purpose selection dialog and returns the selected purpose.
     * @type {function(): Promise<unknown>}
     */
    const getExportPurposeSelection = useCallback(async () => {
        if (!availableExportPurposes.length) {
            return null;
        }
        if (availableExportPurposes.length === 1) {
            return availableExportPurposes[0];
        }
        await Utils.wait(500);
        return new Promise(async (resolve) => {
            let resolved = false;
            setSelectExportPurposeDialogState({
                open: true,
                availablePurposes: availableExportPurposes,
                close: () => {
                    if (!resolved)
                        resolve(null);
                    setSelectExportPurposeDialogState(initialSelectExportPurposeDialogState)
                },
                onSelect: async (purpose) => {
                    resolved = true;
                    return resolve(purpose);
                }
            })
        })
    }, [availableExportPurposes])

    /**
     * Fetches the export information for the price matrices for re-importing.
     * @type {function(): *}
     */
    const getExportInfoForRecordKeeping = useCallback(async () => {
        if (!transformedMatrices?.length) {
            ToastService.warning({title: "Export Failed", message: "No data to export!"});
            return;
        }
        void Utils.exportCsvFile(transformedMatrices.map(({type, target, basedOn, change, matrix}) => {
                return ({
                    "Type": type?.title,
                    "Target": target,
                    "Change": change,
                    "Based on": basedOn,
                    "Start Date": matrix.startDate ? DateUtils.serverToLocal(matrix.startDate).format(DateUtils.dateTimeFormat) : "",
                    "End Date": matrix.endDate ? DateUtils.serverToLocal(matrix.endDate).format(DateUtils.dateTimeFormat) : "",
                });
            }),
            {
                fileTitle: `${defaultCustomer?.name ?? "System"} ${multipleTitle} up until ${DateUtils.localNow().format(DateUtils.customFormats.exportEntriesDate)}`
            });
    }, [defaultCustomer?.name, multipleTitle, transformedMatrices])

    /**
     * Exports the information for re-importing the price matrices.
     * @type {function(): *}
     */
    const getExportInfoForReImporting = useCallback(async () => {
        if (!matrices?.length) {
            ToastService.warning({title: "Export Failed", message: "No data to export!"});
            return;
        }
        void Utils.exportBizImportFile(
            JSON.stringify({
                type: "price-matrix",
                version: '1.0.0',
                data: {
                    matrices: matrices,
                    customer: defaultCustomer?.id ? defaultCustomer : undefined,
                },
            }, null, 4),
            {
                fileTitle: `${defaultCustomer?.name ?? "System"} ${multipleTitle} up until ${DateUtils.localNow().format(DateUtils.customFormats.exportEntriesDate)}`
            })
    }, [defaultCustomer, matrices, multipleTitle])

    /**
     * Fetches the export information for the price matrices.
     * @type {(function(): Promise<undefined | {title: string,body: Array<Record<string, string>>}>)}
     */
    const getExportInfo = useCallback(() => {
        const exportFunction = async () => {
            const purpose = await getExportPurposeSelection();
            if (!purpose)
                return;
            switch (purpose) {
                case selectExportPurposes.recordKeeping:
                    return await getExportInfoForRecordKeeping();
                case selectExportPurposes.reImporting:
                    return await getExportInfoForReImporting();
                default:
                    return;
            }
        }
        void exportFunction();
        return null // returning null to prevent the default export functionality
    }, [getExportPurposeSelection, getExportInfoForRecordKeeping, getExportInfoForReImporting])

    /**
     * Opens the export purpose selection dialog and returns the selected purpose.
     * @type {function(): Promise<unknown>}
     */
    const getImportFileFromUser = useCallback(async () => {
        return new Promise(async (resolve) => {
            let resolved = false;
            setUploadBizImportFileDialogState({
                open: true,
                close: () => {
                    if (!resolved)
                        resolve(null);
                    setUploadBizImportFileDialogState(initialSelectUploadBizImportFileDialogState)
                },
                onSelect: async (purpose) => {
                    resolved = true;
                    return resolve(purpose);
                },
                title: "Import Price Matrices",
                description: `Please select a BizImport file to import ${multipleTitle} for ${defaultCustomer?.name ?? "System"}.`,
                parse: async (file) => JSON.parse(file),
                validate: async (parsedFile) => {
                    if (parsedFile.type !== "price-matrix") {
                        throw new Error("Invalid type of Biz Import file. The file must be a direct result of exporting price matrices for the re-importing purposes.");
                    }
                    if (parsedFile.data.length === 0) {
                        throw new Error("The selected file does not contain any price matrices. It is essentially empty.");
                    }
                    const newMatrices = parsedFile.data.matrices.filter(e => !matrices.find(m => m.id === e.id));
                    if (newMatrices.length === 0) {
                        throw new Error("The selected file only contains price matrices that already exist in the system. Please remove them from the file and try again.");
                    }
                },
            })
        })
    }, [defaultCustomer?.name, multipleTitle, matrices])

    /**
     * Imports the price matrices from the selected file.
     * @type {(function(): Promise<void>)|*}
     */
    const importPriceMatrices = useCallback(async () => {
        const json = await getImportFileFromUser();
        if (!json || !isMounted())
            return;
        const data = json.data;
        let permittedToContinue = true;
        if (data.customer && data.customer.id === defaultCustomer?.id) {
            permittedToContinue = permittedToContinue && await new Promise(resolve => {
                dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                    open: true,
                    title: 'Import Price Matrices',
                    description: `It seems you are trying to import price matrices of the same customer: ${defaultCustomer?.name ?? ""}. We do not recommend this action. Do you want to continue?`,
                    maxWidth: 'sm',
                    fullWidth: true,
                    onClose: (confirmed) => {
                        resolve(confirmed);
                        return true;
                    }
                }));
            });
        }
        if (!data.customer && !defaultCustomer?.id) {
            permittedToContinue = permittedToContinue && await new Promise(resolve => {
                dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                    open: true,
                    title: 'Import Price Matrices',
                    description: `It seems you are trying to import system tier pricing while in system price matrix list. We do not recommend this action. Do you want to continue?`,
                    maxWidth: 'sm',
                    fullWidth: true,
                    onClose: (confirmed) => {
                        resolve(confirmed);
                        return true;
                    }
                }));
            });
        }
        if (!permittedToContinue || !isMounted())
            return;
        setLoading(true);
        for (const entry of (data.matrices ?? [])) {
            const exists = matrices.find(e => e.id === entry.id);
            if (exists) {
                continue;
            }
            const response = await Api.createPriceMatrix({
                ...entry,
                id: undefined,
                customer: null,
                vendor: null,
                ...(defaultCustomer?.id && {customer: {id: defaultCustomer?.id}}),
            });
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                setMatrices(prevState => [...prevState, response.data]);
            }
        }
        if (!isMounted())
            return;
        setLoading(false);
    }, [defaultCustomer?.id, defaultCustomer?.name, dispatch, getImportFileFromUser, isMounted, matrices])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return visibleMatrices.map(({type, target, change, basedOn, matrix}) => {
            const removing = !!removingIds.find(e => e === matrix.id);
            const expired = matrix.endDate ? DateUtils.serverToLocal(matrix.endDate).isBefore(DateUtils.localNow(), 'day') : false;
            return ({
                key: matrix.id,
                data: matrix,
                className: expired ? 'expired-row' : '',
                style: {
                    ...(expired && {
                        color: UIUtils.themeVar('palette-text-disabled'),
                    })
                },
                cells: {
                    ...(canManagePriceMatrices ? {
                        [PriceMatricesDataGridCellKeys.remove]: (
                            <IconButton
                                onPressed={() => removePriceMatrix(matrix.id)}
                                disabled={removing}
                                color={'paper.error'}
                                className={'price-matrices-remove-icon'}
                            >
                                <SvgIcon component={RemoveIcon} className='icon normal'/>
                            </IconButton>
                        ),
                        [PriceMatricesDataGridCellKeys.edit]: (
                            <IconButton
                                onPressed={() => onEditPriceMatrix(matrix)}
                                disabled={removing}
                                color={'paper.secondary'}
                            >
                                <Edit className={"icon"}/>
                            </IconButton>
                        ),
                    } : {}),
                    [PriceMatricesDataGridCellKeys.type]: type?.title,
                    [PriceMatricesDataGridCellKeys.target]:
                        type?.id === PriceMatrixTypes.part.id
                            ? <ProductInformationPopover
                                partNo={target}
                                wrapperProps={{
                                    ...dataGridCellTextProps,
                                }}
                            >
                                {target ?? ""}
                            </ProductInformationPopover>
                            : target,
                    [PriceMatricesDataGridCellKeys.change]: change,
                    [PriceMatricesDataGridCellKeys.basedOn]: basedOn,
                    [PriceMatricesDataGridCellKeys.startDate]: matrix.startDate,
                    [PriceMatricesDataGridCellKeys.endDate]: matrix.endDate,
                },
            });
        })
    }, [canManagePriceMatrices, visibleMatrices, removingIds, removePriceMatrix, onEditPriceMatrix])

    return (
        <>
            <Row className='no-gutters w-100 justify-content-between align-items-center my-4'>
                <p className='text-lg font-weight-semi-bold text-color-1'>
                    {defaultCustomer?.name ?? ""} {multipleTitle}
                </p>
                {
                    canManagePriceMatrices &&
                    <div className={'d-flex align-items-center justify-content-end'}>
                        {/*<Button*/}
                        {/*    variant={'outlined'}*/}
                        {/*    color={'primary'}*/}
                        {/*    onClick={importPriceMatrices}*/}
                        {/*    startIcon={<Excel className='icon small'/>}*/}
                        {/*>*/}
                        {/*    Import*/}
                        {/*</Button>*/}
                        <Button
                            className='ml-3'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={onAddMatrix}
                            startIcon={<Add className='icon small'/>}
                        >
                            Create New
                        </Button>
                    </div>
                }
            </Row>
            <Paper className='paper mb-3 py-3 flex-column w-100' sx={{
                '.expired-row': {
                    ".data-grid-row-body-inner-cell-text": {
                        color: 'text.disabled',
                    },
                },
            }}>
                <Col xs={12} className='px-1'>
                    <SearchInput
                        loading={loading}
                        autoSearch={true}
                        search={setKeywords}
                        label={'Search'}
                        variant={'outlined'}
                    />
                </Col>
                <Col xs={12} className='px-3'>
                    <ToggleButtonGroup
                        disabled={loading}
                        size={'small'}
                        value={viewMode}
                        onChange={(_, v) => toggleViewMode(v)}
                        exclusive
                        fullWidth
                    >
                        <ToggleButton value={"all"} sx={{backgroundColor: viewMode === 'all' ? "divider" : "transparent", border: "none"}}>
                            <RuleRounded sx={{padding: 0.25}}/>
                            All Matrices ({allMatricesLength})
                        </ToggleButton>
                        <ToggleButton value={"active"} sx={{backgroundColor: viewMode === 'active' ? "divider" : "transparent", border: "none"}}>
                            <EventAvailableRounded sx={{padding: 0.25}}/>
                            Active Matrices ({activeMatricesLength})
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Col>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    getExportInfo={canShowMatrixExport ? getExportInfo : undefined}
                    state={{
                        pagination: paginationInfo,
                        loading: {state: loading},
                        density: dataGridDensity
                    }}
                    classNames={{
                        container: 'price-matrices-data-grid-container'
                    }}
                    hideFooterActions
                    onPageSizeChanged={(_, current) => setPageSize(current.pageSize)}
                    onCurrentPageChanged={(_, current) => setCurrentPage(current.currentPage)}
                />
            </Paper>
            {
                canManagePriceMatrices &&
                <UpsertPriceMatrixDialog
                    {...upsertDialogState}
                />
            }
            <SelectExportPurposeDialog
                {...selectExportPurposeDialogState}
            />
            <UploadBizImportFileDialog
                {...uploadBizImportFileDialogState}
            />
        </>
    );
}

export default PriceMatricesListView;
